import { PrimeMember } from 'iam/MemberStore'
import Member from 'iam/models/Member'
import User from 'iam/models/User'
import { Model } from 'infra/model'
import { JobProgression } from 'jobs/progression/JobProgression'
import { PrimeProgression } from 'jobs/progression/JobProgressionStore'
import { Prompt } from 'jobs/prompts/Prompt'
import { PrimePrompt } from 'jobs/prompts/PromptStore'
import once from 'lodash/once'
import { VideoCall } from 'messaging/calling/VideoCall'
import { PrimeVideoCall } from 'messaging/calling/VideoCallStore'
import { action, computed, observable, makeObservable } from 'mobx'
import { Subject } from 'rxjs'
import { Id } from 'types/definitions'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { match } from 'utils/pattern-match-util'
import { ContentDTO, MessageDTO, MessageStatusDTO } from '../dtos/messages'
import { MessageContent, TextContent } from './Content'
import Conversation from './Conversation'
import { MessageDeliveryStatus, MessageStatus } from './MessageStatus'
import { MessageType } from './MessageType'

/**
 * Message.
 */
export default class Message extends Model<MessageDTO, Message> {
  /**
   * Subject used to push status changes.
   */
  private statusChanges = new Subject<MessageStatus>()

  /**
   * Status changes as an observable stream.
   */
  readonly statusChanges$ = this.statusChanges.asObservable()

  conversation: Conversation
  @observable id = Id.none
  @observable recipientStatuses: MessageStatus[] = []
  @observable sender = Member.none
  @observable dateCreated = DateZero()
  @observable dateModified = DateZero()
  @observable messageType = MessageType.Message

  /**
   * The message content.
   */
  @observable.shallow content: MessageContent = {
    type: 'Text',
    text: '',
  }

  /**
   * Default message.
   */
  static get none() {
    return lazyNone()
  }

  constructor(
    conversation: Conversation,
    private primeMember: PrimeMember,
    private primeCall: PrimeVideoCall,
    private primePrompt: PrimePrompt,
    private primeProgression: PrimeProgression
  ) {
    super()
    makeObservable(this)
    this.conversation = conversation
  }

  /**
   * Returns a (Markdown) string that can be used for displaying
   * text for the message, even if it's not a text content type.
   */
  @computed
  get text() {
    const content = this.content
    switch (content.type) {
      case 'Text':
        return content.text
      case 'Call':
        return 'Call started'
      case 'Prompt':
        return 'Prompt'
      case 'Progression':
        return 'Job Progression'
    }
  }

  /**
   * Gets the status for a particular user.
   *
   * @param user the user ID that the status must belong to
   */
  statusFor(user: User): MessageStatus | null {
    const status = this.recipientStatuses.find((s) => s.member.user === user)
    if (status) {
      return status
    }

    return null
  }

  /**
   * Parse the DTO props onto the model.
   *
   * @param attrs
   */
  parse(attrs: Partial<MessageDTO>): Partial<Message> {
    const sender = this.primeMember({
      workspace_id: this.conversation.workspace.id,
      user_id: attrs.user_id,
      user_public_id: attrs.user_public_id,
    })

    return {
      sender,
      id: attrs.id,
      messageType: parseMessageType(attrs.content?.type || 'TEXT'),
      dateCreated: maybeParseDate(attrs.date_created),
      dateModified: maybeParseDate(attrs.date_modified),
      recipientStatuses: attrs.statuses?.map((status) =>
        this.parseMessageStatus(status)
      ),
      // The API way of describing content is not ideal.
      // In the Work Portal, we want Content to mean the actual type of content,
      // and the other flags such as Note and System Message to be attributes.
      // We also currently don't use the File content.
      content: this.parseContent(attrs.content),
    }
  }

  /**
   * Parses the message content
   *
   * @private
   * @param contentDto
   */
  private parseContent(
    contentDto: MessageDTO['content'] | undefined
  ): MessageContent | undefined {
    if (!contentDto) {
      return undefined
    }

    switch (contentDto.type) {
      case 'CALL': {
        return {
          type: 'Call',
          call: this.primeCall(this.conversation, {
            id: contentDto.call_id,
          }),
        }
      }
      case 'EXTERNAL_REFERENCE': {
        // External references have a single external ID and a type.
        switch (contentDto.external_type) {
          // Reference to a Prompt.
          case 'worklayer.jobs.Prompt': {
            return {
              type: 'Prompt',
              prompt: this.primePrompt({
                id: contentDto.external_id,
              }),
            }
          }
          case 'worklayer.jobs.Progression':
            return {
              type: 'Progression',
              stepId: contentDto.external_id,
              progression: this.primeProgression({
                jobId:
                  this.conversation.meta.type === 'Job'
                    ? this.conversation.meta.id
                    : undefined,
              }),
            }
        }
        return createFallbackContent(contentDto.text)
      }
      default: {
        return createFallbackContent(contentDto.text)
      }
    }
  }

  /**
   * Marks the message as delivered for the specified user.
   *
   * @param user
   */
  @action
  markDelivered(user: User) {
    const status = this.statusFor(user)
    if (
      status &&
      status.deliveryStatus !== MessageDeliveryStatus.Delivered &&
      status.deliveryStatus !== MessageDeliveryStatus.Read
    ) {
      status.deliveryStatus = MessageDeliveryStatus.Delivered
      this.statusChanges.next(status)
    }
  }

  /**
   * Marks the message as read for the specified user.
   *
   * @param user
   */
  @action
  markRead(user: User) {
    const status = this.statusFor(user)
    if (status && status.deliveryStatus !== MessageDeliveryStatus.Read) {
      status.deliveryStatus = MessageDeliveryStatus.Read
      this.statusChanges.next(status)
    }
  }

  /**
   * Parse message status from a DTO.
   *
   * @param dto
   */
  private parseMessageStatus(dto: MessageStatusDTO): MessageStatus {
    return {
      id: dto.id,
      message: this,
      deliveryStatus: match(dto.status, {
        SENT: () => MessageDeliveryStatus.Sent,
        DELIVERED: () => MessageDeliveryStatus.Delivered,
        READ: () => MessageDeliveryStatus.Read,
        [match.DEFAULT]: () => MessageDeliveryStatus.None,
      }),
      member:
        this.primeMember({
          workspace_id: this.conversation.workspace.id,
          user_id: dto.user_id,
          user_public_id: dto.user_public_id,
        }) || Member.none,
    }
  }
}

/**
 * Parses the message type.
 *
 * @param type
 */
function parseMessageType(type: ContentDTO['type']): MessageType {
  switch (type) {
    case 'NOTE':
    case 'TEAM_NOTE':
    case 'ADMIN_NOTE':
      return MessageType.Note
    case 'ADMIN':
      return MessageType.AdminMessage
    case 'SYSTEM':
      return MessageType.SystemMessage
    default:
      return MessageType.Message
  }
}

/**
 * Creates fallback content in case we can't parse the content type.
 *
 * @param text
 */
function createFallbackContent(text?: string): TextContent {
  return {
    type: 'Text',
    text: text || '_no message_',
  }
}

const lazyNone = once(
  () =>
    new Message(
      Conversation.none,
      () => Member.none,
      () => VideoCall.none,
      () => Prompt.none,
      () => JobProgression.none
    )
)
